import { useCallback, useEffect, useMemo, useState } from "react";
import { emptyProgress, progressStore, type CourseProgress } from "@/core/progress";
import { countSteps, stepKey, type Course, type StepKey } from "@/core/types";

export interface UseProgressResult {
  progress: CourseProgress;
  loaded: boolean;
  completedCount: number;
  totalCount: number;
  percentage: number;
  isStepDone(moduleId: string, lessonId: string, stepId: string): boolean;
  completedInLesson(moduleId: string, lessonId: string, stepIds: string[]): number;
  markStepDone(moduleId: string, lessonId: string, stepId: string): void;
  rememberLesson(moduleId: string, lessonId: string): void;
  reset(): void;
}

export function useProgress(course: Course): UseProgressResult {
  const [progress, setProgress] = useState<CourseProgress>(emptyProgress);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    void progressStore.load(course.id).then((stored) => {
      if (!active) return;
      setProgress(stored);
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [course.id]);

  const persist = useCallback(
    (next: CourseProgress) => {
      setProgress(next);
      void progressStore.save(course.id, next);
    },
    [course.id],
  );

  const completed = useMemo(() => new Set<StepKey>(progress.completedSteps), [progress]);
  const totalCount = useMemo(() => countSteps(course), [course]);

  const isStepDone = useCallback(
    (moduleId: string, lessonId: string, stepId: string) =>
      completed.has(stepKey(moduleId, lessonId, stepId)),
    [completed],
  );

  const markStepDone = useCallback(
    (moduleId: string, lessonId: string, stepId: string) => {
      const key = stepKey(moduleId, lessonId, stepId);
      if (completed.has(key)) return;
      persist({ ...progress, completedSteps: [...progress.completedSteps, key] });
    },
    [completed, persist, progress],
  );

  const completedInLesson = useCallback(
    (moduleId: string, lessonId: string, stepIds: string[]) =>
      stepIds.filter((id) => completed.has(stepKey(moduleId, lessonId, id))).length,
    [completed],
  );

  const rememberLesson = useCallback(
    (moduleId: string, lessonId: string) => {
      const key = `${moduleId}/${lessonId}`;
      if (progress.lastLessonKey === key) return;
      persist({ ...progress, lastLessonKey: key });
    },
    [persist, progress],
  );

  const reset = useCallback(() => {
    setProgress({ ...emptyProgress });
    void progressStore.reset(course.id);
  }, [course.id]);

  return {
    progress,
    loaded,
    completedCount: completed.size,
    totalCount,
    percentage: totalCount === 0 ? 0 : Math.round((completed.size / totalCount) * 100),
    isStepDone,
    completedInLesson,
    markStepDone,
    rememberLesson,
    reset,
  };
}
